import React, { useState, useRef, useEffect } from 'react';
import { IMessage } from '../../services/messaging/IMessagingProvider';
import { MessageMedia } from './MessageMedia';

interface MessageBubbleProps {
  message: IMessage;
  isOwn: boolean;
  onEdit?: (messageId: string, newText: string) => Promise<void>;
  onDelete: (messageId: string, scope: 'self' | 'everyone') => Promise<void>;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isOwn, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(message.content);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  // Keep editText in sync if content changes (remote edit)
  useEffect(() => { setEditText(message.content); }, [message.content]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (editing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.selectionStart = editInputRef.current.value.length;
    }
  }, [editing]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const formatTimestamp = (timestamp: number | string | Date) => {
    let date: Date;
    if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
      date = new Date(timestamp.replace(' ', 'T')); // no 'Z' — MySQL DATETIME is server-local, not UTC
    } else {
      date = new Date(timestamp);
    }
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const openMenu = () => setMenuOpen(true);

  // Long press for mobile
  const onTouchStart = () => {
    longPressTimer.current = setTimeout(openMenu, 500);
  };
  const onTouchEnd = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };

  const showError = (e: any) => {
    const msg = e?.response?.data?.message || e?.message || 'Something went wrong';
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(null), 4000);
  };

  const handleSaveEdit = async () => {
    if (!onEdit || !editText.trim() || editText.trim() === message.content) { setEditing(false); return; }
    setSaving(true);
    try {
      await onEdit(message.id, editText.trim());
      setEditing(false);
    } catch (e: any) {
      showError(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scope: 'self' | 'everyone') => {
    setDeleteConfirm(false);
    setMenuOpen(false);
    try {
      await onDelete(message.id, scope);
    } catch (e: any) {
      showError(e);
    }
  };

  const bubbleStyle: React.CSSProperties = {
    padding: '10px 14px',
    borderRadius: '18px',
    backgroundColor: isOwn ? 'var(--chat-bubble-color, var(--primary-color))' : 'var(--chat-bubble-received-color, var(--background-paper))',
    color: isOwn ? 'var(--chat-bubble-text, #ffffff)' : 'var(--chat-bubble-received-text, var(--text-primary))',
    border: isOwn ? 'none' : '1px solid var(--border-color)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    position: 'relative',
    cursor: 'default',
    userSelect: 'text',
  };

  return (
    <div style={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', marginBottom: '8px', width: '100%' }}>
      <div
        style={{ position: 'relative', maxWidth: '70%' }}
        onContextMenu={e => { e.preventDefault(); openMenu(); }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchMove={onTouchEnd}
      >
        <div style={bubbleStyle}>
          {/* Editing mode */}
          {editing ? (
            <div>
              <textarea
                ref={editInputRef}
                value={editText}
                onChange={e => setEditText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
                  if (e.key === 'Escape') { setEditing(false); setEditText(message.content); }
                }}
                style={{
                  width: '100%', minHeight: 60, background: 'rgba(0,0,0,0.15)',
                  border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8,
                  color: 'inherit', fontSize: 14, padding: '6px 8px', resize: 'none',
                }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  style={{ flex: 1, padding: '4px 8px', borderRadius: 6, border: 'none', background: 'rgba(255,255,255,0.25)', color: 'inherit', cursor: 'pointer', fontSize: 12 }}
                >
                  {saving ? '…' : 'Save'}
                </button>
                <button
                  onClick={() => { setEditing(false); setEditText(message.content); }}
                  style={{ flex: 1, padding: '4px 8px', borderRadius: 6, border: 'none', background: 'rgba(0,0,0,0.15)', color: 'inherit', cursor: 'pointer', fontSize: 12 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              {message.content && (
                <div style={{ marginBottom: message.mediaUrl ? '8px' : '0' }}>{message.content}</div>
              )}
              {message.mediaUrl && (
                <MessageMedia
                  mediaType={message.mediaType as 'image' | 'video' | 'link' | 'audio'}
                  mediaUrl={message.mediaUrl}
                  thumbnailUrl={message.thumbnailUrl}
                  mediaKey={message.mediaKey}
                  mediaMimeType={message.mediaMimeType}
                  allowDownload={false}
                />
              )}
            </>
          )}

          {/* Inline error */}
          {errorMsg && (
            <div style={{ fontSize: 11, marginTop: 4, padding: '3px 6px', borderRadius: 4, background: 'rgba(244,67,54,0.15)', color: '#ff6b6b', wordBreak: 'break-word' }}>
              {errorMsg}
            </div>
          )}

          {/* Timestamp row */}
          <div style={{ fontSize: 11, marginTop: 4, opacity: 0.7, textAlign: 'right', color: 'var(--info-color,#2196f3)' }}>
            {message.editedAt && <span style={{ marginRight: 4, fontStyle: 'italic' }}>edited</span>}
            {formatTimestamp(message.timestamp)}
            {isOwn && (
              <span style={{ marginLeft: 4, verticalAlign: 'middle' }}>
                <span className="material-icons" style={{ fontSize: 14, verticalAlign: 'middle', color: 'var(--info-color,#2196f3)' }}>
                  {message.status === 'read' ? 'done_all' : 'done'}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Context menu */}
        {menuOpen && (
          <div
            ref={menuRef}
            style={{
              position: 'absolute', zIndex: 1000,
              [isOwn ? 'right' : 'left']: 0,
              bottom: '100%', marginBottom: 4,
              background: 'var(--background-paper)',
              border: '1px solid var(--border-color)',
              borderRadius: 10, overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              minWidth: 170,
            }}
          >
            {onEdit && !message.mediaUrl && (
              <button
                onClick={() => { setEditing(true); setMenuOpen(false); }}
                style={menuItemStyle}
              >
                <span className="material-icons" style={{ fontSize: 16, marginRight: 8 }}>edit</span>
                Edit
              </button>
            )}
            <button
              onClick={() => { handleDelete('self'); }}
              style={menuItemStyle}
            >
              <span className="material-icons" style={{ fontSize: 16, marginRight: 8 }}>visibility_off</span>
              Delete for me
            </button>
            {isOwn && (
              <button
                onClick={() => { setMenuOpen(false); setDeleteConfirm(true); }}
                style={{ ...menuItemStyle, color: 'var(--error-color,#f44336)' }}
              >
                <span className="material-icons" style={{ fontSize: 16, marginRight: 8 }}>delete_forever</span>
                Delete for everyone
              </button>
            )}
          </div>
        )}

        {/* Delete for everyone confirm */}
        {deleteConfirm && (
          <div style={{
            position: 'absolute', zIndex: 1001,
            [isOwn ? 'right' : 'left']: 0,
            bottom: '100%', marginBottom: 4,
            background: 'var(--background-paper)',
            border: '1px solid var(--error-color,#f44336)',
            borderRadius: 10, padding: '12px 16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            minWidth: 200,
          }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-primary)' }}>
              Delete this message for everyone?<br />
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>This cannot be undone.</span>
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleDelete('everyone')} style={{ flex: 1, padding: '6px', borderRadius: 6, border: 'none', background: 'var(--error-color,#f44336)', color: 'white', cursor: 'pointer', fontSize: 12 }}>Delete</button>
              <button onClick={() => setDeleteConfirm(false)} style={{ flex: 1, padding: '6px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const menuItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', width: '100%',
  padding: '10px 14px', border: 'none', background: 'transparent',
  color: 'var(--text-primary)', cursor: 'pointer', fontSize: 14,
  textAlign: 'left',
};
